import * as THREE from 'three';
import { EventBus } from '../core/Events';
import type { World, WorldSaveData } from '../world/World';
import type { Command } from '../world/Commands';
import type { TimeState } from '../core/Time';
import type { WeatherSave } from '../systems/Weather';
import type { CreatureSnapshot } from '../entities/CreatureManager';
import { lambert } from '../world/Assets';

/**
 * Çok oyunculu istemci.
 *
 * Yaklaşım:
 *  - Dünya mutasyonları KOMUT olarak sunucuya gider; sunucu sıralar ve yayınlar.
 *    Uzaktan gelen komutlar world.commands.replay() içinde uygulanır, böylece
 *    tekrar ağa yollanmaz (sonsuz döngü olmaz).
 *  - Hareket 15 Hz gönderilir; uzak oyuncular hedefe doğru YUMUŞATILIR
 *    (lerp), yoksa karakterler zıplayarak ilerler.
 *  - Bağlantı koparsa oyun tek kişilik olarak devam eder; kayıt zaten yereldir.
 */

export interface RemotePlayerState {
  id: string;
  name: string;
  isHost: boolean;
  isObserver: boolean;
  target: THREE.Vector3;
  targetYaw: number;
  health: number;
  group: THREE.Group;
  label: string;
  /** Kol/bacak animasyonu için: sol kol, sağ kol, sol bacak, sağ bacak. */
  limbs: [THREE.Mesh, THREE.Mesh, THREE.Mesh, THREE.Mesh];
  /** Önceki karenin konumu — hızdan (dolayısıyla yürüyüp yürümediğinden) haber verir. */
  lastPos: THREE.Vector3;
  gaitPhase: number;
}

export type MpStatus = 'off' | 'connecting' | 'connected' | 'error' | 'closed' | 'full';

export interface MultiplayerState {
  creatures: CreatureSnapshot[];
  time?: TimeState;
  weather?: WeatherSave;
}

export interface MultiplayerSnapshot extends MultiplayerState {
  version: 1;
  world: WorldSaveData;
  time: TimeState;
  weather: WeatherSave;
}

type MpEvents = {
  'status:changed': { status: MpStatus; message?: string };
  'players:changed': { count: number };
  chat: { name: string; text: string };
  system: { text: string };
  'world:params': { seed: number; mode: string; difficulty: string; name: string };
  'world:state': MultiplayerState;
  'world:snapshot': MultiplayerSnapshot;
  'creature:damage-intent': { targetId: string; amount: number; by: string };
  'creature:reward': { targetId: string; name: string; items: Array<{ item: string; count: number }>; kogox: number };
};

const POS_HZ = 15;

export class Multiplayer {
  readonly events = new EventBus<MpEvents>();
  status: MpStatus = 'off';
  statusMessage = '';
  roomCode = '';
  isHost = false;
  isObserver = false;
  myId = '';
  private ws: WebSocket | null = null;
  private remotes = new Map<string, RemotePlayerState>();
  private world: World | null = null;
  private posTimer = 0;
  private creatureTimer = 0;
  private stateTimer = 0;
  private snapshotTimer = 0;
  private makeState: (() => MultiplayerState) | null = null;
  private makeSnapshot: (() => MultiplayerSnapshot) | null = null;
  private unsubscribe: (() => void) | null = null;
  private pending: Command[] = [];
  private localIntentIds = new Set<string>();
  /** Host yetkisini kanıtlayan, yalnız host istemcide tutulan anahtar. */
  private hostKey = '';

  constructor(private baseUrl: string) {}

  get connected(): boolean {
    return this.status === 'connected';
  }

  get playerCount(): number {
    return this.remotes.size + (this.connected ? 1 : 0);
  }

  remoteList(): RemotePlayerState[] {
    return [...this.remotes.values()];
  }

  remotePosition(id: string): THREE.Vector3 | null {
    return this.remotes.get(id)?.target.clone() ?? null;
  }

  /** Yüksek entropili, paylaşılabilir oda erişim kodu. */
  static newRoomCode(): string {
    const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let out = '';
    const buf = new Uint8Array(24);
    crypto.getRandomValues(buf);
    for (const b of buf) out += abc[b % abc.length];
    return out;
  }

  private static newHostKey(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Odaya bağlanır.
   * host=true ise dünya parametrelerini bu istemci belirler.
   */
  connect(opts: {
    world: World;
    code: string;
    name: string;
    host: boolean;
    meta: { seed: number; mode: string; difficulty: string; name: string };
    makeState?: () => MultiplayerState;
    makeSnapshot?: () => MultiplayerSnapshot;
    observerTicket?: string;
  }): void {
    this.disconnect();
    this.world = opts.world;
    this.makeState = opts.makeState ?? null;
    this.makeSnapshot = opts.makeSnapshot ?? null;
    this.posTimer = this.creatureTimer = this.stateTimer = this.snapshotTimer = 0;
    this.roomCode = opts.code.toUpperCase();
    this.isHost = opts.host;
    this.isObserver = !!opts.observerTicket;
    if (opts.host) this.hostKey ||= Multiplayer.newHostKey();
    else this.hostKey = '';
    this.setStatus('connecting');

    const q = new URLSearchParams({
      name: opts.name,
      host: opts.host ? '1' : '0',
      ...(opts.host ? { hostKey: this.hostKey } : {}),
      seed: String(opts.meta.seed),
      mode: opts.meta.mode,
      difficulty: opts.meta.difficulty,
      world: opts.meta.name,
      ...(opts.observerTicket ? { observerTicket: opts.observerTicket } : {}),
    });
    const url = `${this.baseUrl.replace(/^http/, 'ws')}/room/${this.roomCode}?${q.toString()}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      this.setStatus('error', String(err).slice(0, 120));
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.setStatus('connected');
      // Bağlantı kurulmadan önce biriken komutları yolla
      for (const cmd of this.pending) this.sendCommand(cmd);
      this.pending = [];
      if (this.isHost) this.sendSnapshot();
    };
    ws.onclose = (ev) => {
      if (this.status !== 'full') {
        const reason = ev.reason || 'Bağlantı kapandı, tek kişilik devam ediyor.';
        this.setStatus(reason === 'Host ayrıldı' ? 'closed' : 'closed', reason);
      }
      this.clearRemotes();
    };
    ws.onerror = () => {
      this.setStatus('error', 'Bağlantı hatası.');
    };
    ws.onmessage = (ev) => {
      try {
        this.onMessage(JSON.parse(String(ev.data)) as Record<string, unknown>);
      } catch {
        /* bozuk paket yoksayılır */
      }
    };

    // Gözlemci dünya komut darboğazına hiç abone olmaz; sunucu da tüm
    // mutasyon paketlerini rol bazında reddeder.
    if (!this.isObserver) this.unsubscribe = opts.world.commands.onApplied((cmd, result) => {
      if (!result.ok) return;
      this.sendCommand(cmd);
    });
  }

  rebindWorld(world: World): void {
    this.unsubscribe?.();
    this.world = world;
    if (this.isObserver) {
      this.unsubscribe = null;
      return;
    }
    this.unsubscribe = world.commands.onApplied((cmd, result) => {
      if (result.ok) this.sendCommand(cmd);
    });
  }

  disconnect(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* yoksay */
      }
      this.ws = null;
    }
    this.clearRemotes();
    this.pending = [];
    this.localIntentIds.clear();
    this.makeState = null;
    this.makeSnapshot = null;
    if (this.status !== 'off') this.setStatus('off');
  }

  private clearRemotes(): void {
    for (const r of this.remotes.values()) r.group.removeFromParent();
    this.remotes.clear();
    this.events.emit('players:changed', { count: this.playerCount });
  }

  private sendCommand(cmd: Command): void {
    if (this.isObserver) return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (this.status === 'connecting') this.pending.push(cmd);
      return;
    }
    try {
      if (this.isHost) {
        this.ws.send(JSON.stringify({ t: 'cmd', cmd }));
      } else {
        const intentId = crypto.randomUUID();
        this.localIntentIds.add(intentId);
        this.ws.send(JSON.stringify({ t: 'intent', intent: { kind: 'world', cmd: { ...cmd, intentId } } }));
      }
    } catch {
      /* yoksay */
    }
  }

  /** Guest saldırısını authoritative hosta yollar; guest yerelde hasar/loot uygulamaz. */
  sendCreatureDamageIntent(targetId: string, amount: number): void {
    if (this.isObserver || this.isHost || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const safeAmount = Math.max(0, Math.min(200, Number.isFinite(amount) ? amount : 0));
    if (!targetId || safeAmount <= 0) return;
    try { this.ws.send(JSON.stringify({ t: 'intent', intent: { kind: 'creature:damage', targetId, amount: safeAmount } })); } catch { /* yoksay */ }
  }

  /** Hostun authoritative kill ödülünü yalnız saldıran guest'e yollar. */
  sendCreatureReward(to: string, reward: { targetId: string; name: string; items: Array<{ item: string; count: number }>; kogox: number }): void {
    if (!this.isHost || !this.ws || this.ws.readyState !== WebSocket.OPEN || !to) return;
    try { this.ws.send(JSON.stringify({ t: 'award', to, reward })); } catch { /* yoksay */ }
  }

  /** Reddedilen optimistic guest komutunu host dünya snapshot'ıyla düzeltir. */
  private sendWorldSync(to: string): void {
    if (!this.isHost || !this.makeSnapshot || !this.ws || this.ws.readyState !== WebSocket.OPEN || !to) return;
    try { this.ws.send(JSON.stringify({ t: 'sync', to, snapshot: this.makeSnapshot() })); } catch { /* yoksay */ }
  }

  /** Host, DO'nun güvenilir konumuna göre guest dünya intentinin erişimini doğrular. */
  private canApplyWorldIntent(cmd: Command, position: { x: number; y: number; z: number }): boolean {
    const world = this.world;
    if (!world || ![position.x, position.y, position.z].every(Number.isFinite)) return false;
    const near = (x: number, y: number, z: number, reach = 14) => Math.hypot(x - position.x, y - position.y, z - position.z) <= reach;
    if (cmd.t === 'structure:place') return near(cmd.x, cmd.y, cmd.z);
    if (cmd.t === 'resource:harvest') {
      const resource = world.resourceAt(cmd.id)?.instance;
      return !!resource && resource.def === cmd.def && near(resource.x, resource.y, resource.z, 8);
    }
    if (cmd.t === 'creature:spawn' || cmd.t === 'creature:damage' || cmd.t === 'creature:despawn') return false;
    const structure = world.structureById(cmd.id);
    return !!structure && near(structure.x, structure.y, structure.z, 10);
  }

  private sendSnapshot(): void {
    if (!this.isHost || !this.makeSnapshot || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify({ t: 'snapshot', snapshot: this.makeSnapshot() })); } catch { /* yoksay */ }
  }

  private sendState(includeTime: boolean): void {
    if (!this.isHost || !this.makeState || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const state = this.makeState();
    if (!includeTime) state.time = state.weather = undefined;
    try { this.ws.send(JSON.stringify({ t: 'state', state })); } catch { /* yoksay */ }
  }

  sendChat(text: string): void {
    if (this.isObserver || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ t: 'chat', text }));
  }

  private onMessage(msg: Record<string, unknown>): void {
    const world = this.world;
    if (!world) return;

    switch (msg.t) {
      case 'welcome': {
        this.myId = String(msg.you ?? '');
        this.isObserver = msg.role === 'observer';
        const meta = msg.meta as { seed: number; mode: string; difficulty: string; name: string } | null;
        if (meta && !this.isHost) this.events.emit('world:params', meta);
        for (const p of (msg.players as Array<Record<string, unknown>>) ?? []) this.addRemote(p);
        const snapshot = msg.snapshot as MultiplayerSnapshot | null;
        if (snapshot && !this.isHost) this.events.emit('world:snapshot', snapshot);
        // Snapshot sonrasındaki komutlar geç katılanı güncel başa taşır.
        const history = (msg.history as Command[]) ?? [];
        const activeWorld = this.world;
        activeWorld?.commands.replay(() => {
          for (const cmd of history) activeWorld.apply(cmd);
        });
        this.events.emit('players:changed', { count: this.playerCount });
        break;
      }
      case 'join': {
        this.addRemote((msg.player as Record<string, unknown>) ?? {});
        this.events.emit('players:changed', { count: this.playerCount });
        break;
      }
      case 'leave': {
        const r = this.remotes.get(String(msg.id));
        if (r) {
          r.group.removeFromParent();
          this.remotes.delete(r.id);
        }
        this.events.emit('players:changed', { count: this.playerCount });
        break;
      }
      case 'pos': {
        const r = this.remotes.get(String(msg.id));
        if (!r) {
          this.addRemote(msg);
          break;
        }
        const x = Number(msg.x);
        const y = Number(msg.y);
        const z = Number(msg.z);
        if (![x, y, z].every(Number.isFinite)) break;
        r.target.set(x, y, z);
        r.targetYaw = Number(msg.yaw) || 0;
        r.health = Number(msg.health) || r.health;
        break;
      }
      case 'cmd': {
        const cmd = msg.cmd as Command & { intentId?: string };
        if (!cmd || typeof cmd.t !== 'string' || this.isHost) break;
        if (cmd.intentId && this.localIntentIds.delete(cmd.intentId)) break;
        // Authoritative host sonucu: replay ile uygula, tekrar ağa gönderme.
        world.commands.replay(() => world.apply(cmd));
        break;
      }
      case 'intent': {
        if (!this.isHost) break;
        const intent = msg.intent as Record<string, unknown> | null;
        const by = String(msg.by ?? '');
        const rawPosition = msg.position as Record<string, unknown> | null;
        const position = { x: Number(rawPosition?.x), y: Number(rawPosition?.y), z: Number(rawPosition?.z) };
        if (!intent || !by) break;
        if (intent.kind === 'world') {
          const cmd = intent.cmd as Command;
          const accepted = !!cmd && typeof cmd.t === 'string' && this.canApplyWorldIntent(cmd, position) && world.apply({ ...cmd, by } as Command).ok;
          if (!accepted) this.sendWorldSync(by);
        } else if (intent.kind === 'creature:damage') {
          const targetId = String(intent.targetId ?? '');
          const amount = Number(intent.amount);
          if (targetId && Number.isFinite(amount)) this.events.emit('creature:damage-intent', { targetId, amount, by });
        }
        break;
      }
      case 'sync': {
        const snapshot = msg.snapshot as MultiplayerSnapshot | null;
        if (snapshot && !this.isHost) {
          this.localIntentIds.clear();
          this.events.emit('world:snapshot', snapshot);
        }
        break;
      }
      case 'award': {
        if (this.isHost || this.isObserver) break;
        const reward = msg.reward as Record<string, unknown> | null;
        if (!reward || typeof reward.targetId !== 'string' || typeof reward.name !== 'string' || !Array.isArray(reward.items)) break;
        const items = reward.items
          .filter((entry): entry is { item: string; count: number } => !!entry && typeof entry === 'object' && typeof (entry as { item?: unknown }).item === 'string' && Number.isInteger((entry as { count?: unknown }).count))
          .map((entry) => ({ item: entry.item, count: Math.max(1, Math.min(999, entry.count)) }));
        this.events.emit('creature:reward', { targetId: reward.targetId, name: reward.name, items, kogox: Math.max(0, Math.min(10000, Number(reward.kogox) || 0)) });
        break;
      }
      case 'state': {
        const state = msg.state as MultiplayerState | null;
        if (state && !this.isHost && Array.isArray(state.creatures)) this.events.emit('world:state', state);
        break;
      }
      case 'snapshot:ack': {
        if (msg.ok !== true) this.setStatus('error', String(msg.reason ?? 'Snapshot reddedildi').slice(0, 160));
        break;
      }
      case 'state:ack': {
        if (msg.ok !== true) this.setStatus('error', String(msg.reason ?? 'State reddedildi').slice(0, 160));
        break;
      }
      case 'snapshot': {
        // Snapshot canlı yayın değildir; yalnız welcome içinde checkpoint alınır.
        break;
      }
      case 'chat': {
        this.events.emit('chat', { name: String(msg.name ?? ''), text: String(msg.text ?? '') });
        break;
      }
      case 'system': {
        this.events.emit('system', { text: String(msg.text ?? '') });
        break;
      }
      default:
        break;
    }
  }

  private addRemote(data: Record<string, unknown>): void {
    const id = String(data.id ?? '');
    if (!id || id === this.myId || this.remotes.has(id)) return;
    const world = this.world;
    if (!world) return;

    const group = new THREE.Group();
    const observer = data.isObserver === true || data.role === 'observer';
    const skin = lambert(0xffccbc);
    const cloth = lambert(observer ? 0xffb300 : 0x7c4dff);
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.95, 0.4), cloth);
    body.position.y = 1.05;
    group.add(body);
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.44, 0.44), skin);
    head.position.y = 1.78;
    group.add(head);
    const armMeshes: THREE.Mesh[] = [];
    for (const sx of [-0.42, 0.42]) {
      // Pivot omuzda kalsın diye geometri aşağı kaydırılır (öteki yerde bacaklarda da aynı desen kullanılıyor).
      const geo = new THREE.BoxGeometry(0.17, 0.8, 0.17);
      geo.translate(0, -0.4, 0);
      const arm = new THREE.Mesh(geo, skin);
      arm.position.set(sx, 1.45, 0);
      group.add(arm);
      armMeshes.push(arm);
    }
    const legMeshes: THREE.Mesh[] = [];
    for (const sx of [-0.16, 0.16]) {
      const geo = new THREE.BoxGeometry(0.2, 0.62, 0.2);
      geo.translate(0, -0.31, 0);
      const leg = new THREE.Mesh(geo, lambert(0x263238));
      leg.position.set(sx, 0.62, 0);
      group.add(leg);
      legMeshes.push(leg);
    }
    const x = Number(data.x) || 0;
    const y = Number(data.y) || 0;
    const z = Number(data.z) || 0;
    group.position.set(x, y, z);
    world.scene.add(group);

    this.remotes.set(id, {
      id,
      name: String(data.name ?? 'Gezgin').slice(0, 24),
      isHost: data.isHost === true,
      isObserver: observer,
      target: new THREE.Vector3(x, y, z),
      targetYaw: Number(data.yaw) || 0,
      health: Number(data.health) || 100,
      group,
      label: String(data.name ?? 'Gezgin'),
      limbs: [armMeshes[0]!, armMeshes[1]!, legMeshes[0]!, legMeshes[1]!],
      lastPos: new THREE.Vector3(x, y, z),
      gaitPhase: 0,
    });
  }

  /** Her karede: konum gönder ve uzak oyuncuları yumuşat. */
  update(dt: number, pos: THREE.Vector3, yaw: number, health: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.posTimer += dt;
      if (this.isHost) {
        this.creatureTimer += dt;
        this.stateTimer += dt;
        this.snapshotTimer += dt;
        if (this.creatureTimer >= 0.1) {
          this.creatureTimer %= 0.1;
          const includeTime = this.stateTimer >= 0.5;
          if (includeTime) this.stateTimer %= 0.5;
          this.sendState(includeTime);
        }
        if (this.snapshotTimer >= 20) {
          this.snapshotTimer %= 20;
          this.sendSnapshot();
        }
      }
      if (this.posTimer >= 1 / POS_HZ) {
        this.posTimer = 0;
        try {
          this.ws.send(JSON.stringify({ t: 'pos', x: pos.x, y: pos.y, z: pos.z, yaw, health }));
        } catch {
          /* yoksay */
        }
      }
    }
    const k = Math.min(1, dt * 12);
    for (const r of this.remotes.values()) {
      r.group.position.lerp(r.target, k);
      let d = r.targetYaw - r.group.rotation.y;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      r.group.rotation.y += d * k;

      // Yürüyüş animasyonu: kare içi gerçek konum değişimine bakılır (network
      // gecikmesi/lerp'ten bağımsız, göze doğru görünsün diye gerçek hız kullanılır).
      const moveDist = r.group.position.distanceTo(r.lastPos);
      r.lastPos.copy(r.group.position);
      const speed = dt > 0 ? moveDist / dt : 0;
      const walking = speed > 0.35;
      if (walking) r.gaitPhase += dt * Math.min(speed, 8) * 2.6;
      else r.gaitPhase += (0 - (r.gaitPhase % (Math.PI * 2))) * Math.min(1, dt * 8);

      const [armL, armR, legL, legR] = r.limbs;
      const swing = walking ? 0.6 : 0;
      armL.rotation.x = Math.sin(r.gaitPhase) * swing;
      armR.rotation.x = Math.sin(r.gaitPhase + Math.PI) * swing;
      legL.rotation.x = Math.sin(r.gaitPhase + Math.PI) * swing;
      legR.rotation.x = Math.sin(r.gaitPhase) * swing;
    }
  }

  private setStatus(status: MpStatus, message = ''): void {
    if (this.status === status && this.statusMessage === message) return;
    this.status = status;
    this.statusMessage = message;
    this.events.emit('status:changed', { status, message });
  }
}
